import {
    FormUiComponentType,
    ObjectFieldDataType,
} from '@components/pages/setup/WorkFlowBuilder/utils/constants';

export const ADDRESS_BOOK_OBJECT_TYPE = 'ADDRESSBOOK';

interface ICustomFieldOption {
    label: string;
    value: string;
}

interface ICustomFieldDef {
    id: string;
    prettyName?: string;
    label?: string;
    isCustom?: boolean;
    dataType?: string;
    formUIComponent?: {
        type?: string;
        values?: ICustomFieldOption[];
        multiselect?: boolean;
        searchStrategy?: string;
        searchSelectKey?: string;
        searchThresholdLength?: number;
    };
}

const CUSTOM_TYPE_MAP: Record<string, string> = {
    [FormUiComponentType.Text]: 'string',
    [FormUiComponentType.String]: 'string',
    [FormUiComponentType.Number]: 'number',
    [FormUiComponentType.Boolean]: 'boolean',
    [ObjectFieldDataType.Datetime]: 'datetime',
    [FormUiComponentType.Dropdown]: 'select',
    [FormUiComponentType.JsonArray]: 'jsonArray',
    [FormUiComponentType.BackendSearch]: 'backend_search',
};

const getCustomFieldLabel = (field: ICustomFieldDef) =>
    field.prettyName || field.label || field.id;

// Maps Object Builder custom field defs into the field-def shape the static
// address-book lists use, so table/form/details/csv consume them unchanged.
export const getCustomFieldList = (customFields: ICustomFieldDef[] = []) =>
    customFields.map((field) => {
        const ui = field.formUIComponent || {};
        const uiType = ui.type ?? field.dataType;
        const base = {
            id: field.id,
            name: getCustomFieldLabel(field),
            type: (uiType && CUSTOM_TYPE_MAP[uiType]) || 'string',
        };
        if (uiType === FormUiComponentType.Dropdown) {
            return { ...base, options: ui.values || [], multiSelect: !!ui.multiselect };
        }
        if (uiType === FormUiComponentType.BackendSearch) {
            return {
                ...base,
                searchStrategy: ui.searchStrategy,
                searchSelectKey: ui.searchSelectKey,
                searchThresholdLength: ui.searchThresholdLength,
                multiSelect: !!ui.multiselect,
            };
        }
        return base;
    });

export const addressRegisterFieldsList =  [
    {
        name: 'Address Code',
        id: 'address_id',
        width: 130,
    },
    {
        name: 'Name',
        id: 'name',
        width: 200,
    },
    {
        name: 'Address',
        id: 'address_detail',
        width: 200,
    },
    {
        name: 'Pincode',
        id: 'pincode',
        width: 130,
    },
    {
        name: 'City',
        id: 'city',
        width: 130,
    },
    {
        name: 'State',
        id: 'state',
        width: 130,
    },
    {
        name: 'Country',
        id: 'country',
        width: 130,
    },
    {
        name: 'Phone',
        id: 'phone',
        width: 130,
    },
    {
        name: 'Alternate Phone',
        id: 'alternate_phone',
        width: 130,
    },
    {
        name: 'Notes',
        id: 'notes',
        width: 200,
    },
    {
        name: 'Company Name',
        id: 'company_name',
        width: 130,
    },
    {
        name: 'Email',
        id: 'email',
        width: 200,
    },
    {
        name: 'Address Status',
        id: 'status',
        width: 130,
    },
    {
        name: 'Latitude',
        id: 'latitude',
    },
    {
        name: 'Longitude',
        id: 'longitude',
    },
    {
        name: 'Location Picture',
        id: 'location_image',
    },
    {
        name: 'Fixed Time Service(Mins)',
        id: 'service_time',
    },
    {
        name: 'Delivery/Pickup Time Slot',
        id: 'time_slot_list',
    },
    {
        name: 'National Address Code',
        id: 'national_address_code',
        width: 180,
    },
    {
        name: 'Constraint Tags',
        id: 'constraint_tags',
        width: 180,
    },
    {
        name: 'Grace Time (sec)',
        id: 'grace_time',
        width: 130,
    },
    {
        name: 'Dock Capacity',
        id: 'dock_capacity',
        width: 120,
    },
    {
        name: 'Dock Vehicle Mapping',
        id: 'dock_vehicle_mapping',
        width: 200,
    },
    {
        name: 'Created By',
        id: 'created_by',
        width: 150,
    },
    {
        name: 'Updated By',
        id: 'updated_by',
        width: 150,
    },
];

// Static mapping of backend keys to CSV headers (matches getSampleCSVColumnList from backend)
const metaDataMapping = {
    address_id: 'Address Code',
    address_line_1: 'Address Line 1',
    address_line_2: 'Address Line 2',
    address_line_3: 'Address Line 3',
    address_line_4: 'Address Line 4',
    address_line_5: 'Address Line 5',
    pincode: 'Pincode',
    city: 'City',
    state: 'State',
    country: 'Country',
    status: 'Address Status',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    latitude: 'Latitude',
    longitude: 'Longitude',
    location_image: 'Location Picture',
    service_time: 'Fixed Service Time(mins)',
    time_slot_list: 'Delivery/Pickup Time Slot',
    company_name: 'Company Name',
    alternate_phone: 'Alternate Phone',
    notes: 'Notes',
    national_address_code: 'National Address Code',
    constraint_tags: 'Constraint Tags',
    grace_time: 'Grace Time (sec)',
    dock_capacity: 'Dock Capacity',
    dock_vehicle_mapping: 'Dock Vehicle Mapping',
};

export const csvKeyMapping = {
    address_id: 'Address Code',
    address_line_1: 'Address Line 1',
    address_line_2: 'Address Line 2',
    address_line_3: 'Address Line 3',
    address_line_4: 'Address Line 4',
    address_line_5: 'Address Line 5',
    pincode: 'Pincode',
    city: 'City',
    state: 'State',
    country: 'Country',
    status: 'Address Status',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    latitude: 'Latitude',
    longitude: 'Longitude',
    service_time: 'Fixed Service Time(mins)',
    time_slot_list: 'Delivery/Pickup Time Slot',
    company_name: 'Company Name',
    alternate_phone: 'Alternate Phone',
    notes: 'Notes',
    national_address_code: 'National Address Code',
    constraint_tags: 'Constraint Tags',
    grace_time: 'Grace Time (sec)',
    dock_capacity: 'Dock Capacity',
    dock_vehicle_mapping: 'Dock Vehicle Mapping',
};

export const getDynamicCsvKeyMapping = (masterdata, customFields = []) => {

    const masterFieldsList = masterdata?.location_master_config?.fields_list || [];
    const fieldsList = masterFieldsList
        .filter(row => !row.hide_in_csv)
        .map(row => row.key);

    const mapping = {
        ...metaDataMapping,
        ...Object.fromEntries(
            customFields.map((field) => [field.id, getCustomFieldLabel(field)]),
        ),
    };

    const dynamicMapping = {};
    for (const key in mapping) {
        if (fieldsList.includes(key)) {
            dynamicMapping[key] = mapping[key];
        }
    }
    return dynamicMapping;
};

export const formFieldList = [
    {
        id: 'basic_details',
        name: 'Basic Details',
        fields_list: [
            {
                name: 'Address Code',
                id: 'address_id',
                type: 'string',
            },
            {
                name: 'Verification Status',
                id: 'status',
                type: 'boolean',
                checkedChildren: 'Verified',
                unCheckedChildren: 'Unverified',
                defaultChecked: false,
            },
            {
                name: 'Address Line 1',
                id: 'address_line_1',
                type: 'string',
            },
            {
                name: 'Address Line 2',
                id: 'address_line_2',
                type: 'string',
            },
            {
                name: 'Address Line 3',
                id: 'address_line_3',
                type: 'string',
            },
            {
                name: 'Address Line 4',
                id: 'address_line_4',
                type: 'string',
            },
            {
                name: 'Address Line 5',
                id: 'address_line_5',
                type: 'string',
            },
            {
                name: 'Name',
                id: 'name',
                type: 'string',
            },
            {
                name: 'Phone',
                id: 'phone',
                type: 'string',
            },
            {
                name: 'Alternate Phone',
                id: 'alternate_phone',
                type: 'string',
            },
            {
                name: 'Notes',
                id: 'notes',
                type: 'string',
            },
            {
                name: 'Company Name',
                id: 'company_name',
                type: 'string',
            },
            {
                name: 'Email',
                id: 'email',
                type: 'email',
            },
            {
                name: 'National Address Code',
                id: 'national_address_code',
                type: 'string',
            },
            {
                name: 'Constraint Tags',
                id: 'constraint_tags',
                type: 'select',
                multiSelect: true,
                optionsConfigKey: 'valid_constraint_tags',
            },
        ],
    },
    {
        id: 'location',
        name: 'Location',
        fields_list: [
            {
                name: 'Pincode',
                id: 'pincode',
                type: 'string',
            },
            {
                name: 'State',
                id: 'state',
                type: 'string',
            },
            {
                name: 'City',
                id: 'city',
                type: 'string',
            },
            {
                name: 'Country',
                id: 'country',
                type: 'string',
            },
            {
                name: 'Latitude',
                id: 'latitude',
                type: 'number',
            },
            {
                name: 'Longitude',
                id: 'longitude',
                type: 'number',
            },
            {
                name: 'Location Picture',
                id: 'location_image',
                type: 'image_upload',
            },
        ],
    },
    {
        id: 'time_slot',
        name: 'Time Slot',
        fields_list: [
            {
                name: 'Service Time(Mins)',
                id: 'service_time',
                type: 'string',
            },
            {
                name: 'Delivery/Pickup Time Slot',
                id: 'time_slot_list',
                type: 'timeRangeList',
                format: 'HH:mm',
            },
        ],
    },
    {
        id: 'dock_configuration',
        name: 'Dock Configuration',
        fields_list: [
            {
                name: 'Grace Time (sec)',
                id: 'grace_time',
                type: 'graceTime',
                hideLabel: false,
            },
            {
                name: 'Dock Capacity',
                id: 'dock_capacity',
                type: 'dockCapacity',
                hideLabel: false,
            },
            {
                name: 'Dock Vehicle Mapping',
                id: 'dock_vehicle_mapping',
                type: 'dockVehicleMapping',
                hideLabel: false,
            },
        ],
    },
];

export const detailColumnsIdSectionWise = [
    {
        key: 'basic_details',
        name: 'Basic Details',
        columns: [
            'address_id',
            'address_detail',
            'status',
            'pincode',
            'city',
            'name',
            'email',
            'state',
            'country',
            'phone',
            'alternate_phone',
            'notes',
            'company_name',
            'national_address_code',
            'constraint_tags',
        ],
    },
    {
        key: 'timeslot',
        name: 'Timeslot',
        columns: [
            'service_time',
            'time_slot_list',
        ],
    },
    {
        key: 'dock_configuration',
        name: 'Dock Configuration',
        columns: [
            'grace_time',
            'dock_capacity',
            'dock_vehicle_mapping',
        ],
    },
    {
        key: 'audit_info',
        name: 'Audit Information',
        columns: [
            'created_by',
            'updated_by',
        ],
    },
];
